import { createTheme } from '@mui/material/styles';

// Create and export the application theme with Mountain Trail colorway
const theme = createTheme({
  palette: {
    primary: {
      main: '#2C5F4F', // Forest Green - earthy, grounded, endurance
      dark: '#1A3A2E', // Deep Forest - authority, depth
      light: '#4A8B7A', // Sage Green - calmer, natural
    },
    secondary: {
      main: '#8B6F47', // Trail Brown - earth, stability
      dark: '#6B5537',
      light: '#A58B68',
    },
    error: {
      main: '#C62828', // Dark Red - warnings, critical
    },
    warning: {
      main: '#F57C00', // Amber - important info
    },
    info: {
      main: '#1976D2', // Blue - informational
    },
    success: {
      main: '#357A38', // Green - positive feedback
    },
    background: {
      default: '#F5F7F5', // Soft off-white with green tint
      paper: '#FFFFFF', // Pure white cards for contrast
    },
    text: {
      primary: '#1A3A2E', // Deep Forest for readability
      secondary: '#5A6B5F', // Muted green-gray for supporting text
    },
    divider: '#D4DDD7', // Soft green-gray
    // Accent color for CTAs (accessed via theme.palette.accent)
    accent: {
      main: '#D97642', // Sunset Orange - energy for key actions
      dark: '#C45F2B',
      light: '#E49B6F',
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      color: '#1F2937',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.3,
      color: '#1F2937',
    },
    h3: {
      fontWeight: 700,
      fontSize: '1.5rem',
      lineHeight: 1.4,
      color: '#1F2937',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
      color: '#1F2937',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.5,
      color: '#1F2937',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
      color: '#1F2937',
    },
    body1: {
      lineHeight: 1.6,
      color: '#1F2937',
    },
    body2: {
      lineHeight: 1.6,
      color: '#6B7280',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)', // Softer, more subtle shadow
          borderRadius: 12, // Increased from 8px
          transition: 'box-shadow 0.3s ease-in-out, transform 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)', // Elevated on hover
          },
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        },
        elevation2: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
        },
        elevation3: {
          boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 8,
          padding: '10px 20px',
          transition: 'all 0.15s ease-in-out',
          // Ensure minimum touch target size for mobile (48px)
          minHeight: 48,
          '@media (max-width: 600px)': {
            minHeight: 48,
            minWidth: 48,
          },
          '&:hover': {
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        contained: {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.12)',
          },
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: '#1A3A2E', // Deep forest on hover
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
            backgroundColor: 'rgba(44, 95, 79, 0.04)', // Forest green tint
          },
        },
        outlinedPrimary: {
          borderColor: '#2C5F4F',
          color: '#2C5F4F',
          '&:hover': {
            borderColor: '#1A3A2E',
            backgroundColor: 'rgba(44, 95, 79, 0.04)',
          },
        },
        sizeSmall: {
          padding: '6px 16px',
          // Even "small" buttons should meet touch target minimums on mobile
          '@media (max-width: 600px)': {
            minHeight: 44,
            padding: '8px 16px',
          },
        },
        sizeLarge: {
          padding: '12px 28px',
          fontSize: '1rem',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          // Ensure minimum touch target size for mobile (48px)
          '@media (max-width: 600px)': {
            minHeight: 48,
            minWidth: 48,
            padding: 12,
          },
        },
        sizeSmall: {
          '@media (max-width: 600px)': {
            minHeight: 44,
            minWidth: 44,
            padding: 10,
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          // FABs already meet 56px minimum, but ensure on mobile
          '@media (max-width: 600px)': {
            minHeight: 56,
            minWidth: 56,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: '#D4DDD7',
          padding: '12px 16px', // Slightly reduced vertical padding
        },
        head: {
          fontWeight: 600,
          backgroundColor: '#2C5F4F', // Forest green header
          color: '#ffffff',
          fontSize: '0.75rem', // Slightly smaller for more compact headers
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          padding: '12px 16px',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.15s ease-in-out',
          '&:nth-of-type(odd)': {
            backgroundColor: '#FFFFFF',
          },
          '&:nth-of-type(even)': {
            backgroundColor: '#F5F7F5', // Subtle green-tinted zebra striping
          },
          '&:hover': {
            backgroundColor: 'rgba(44, 95, 79, 0.08)', // Forest green tint on hover
            cursor: 'pointer',
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          overflow: 'hidden',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          transition: 'all 0.2s ease-in-out',
        },
        filled: {
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          },
        },
        outlined: {
          borderWidth: 2,
        },
        outlinedPrimary: {
          borderColor: '#2C5F4F',
          color: '#2C5F4F',
          '&:hover': {
            backgroundColor: 'rgba(44, 95, 79, 0.08)',
          },
        },
        outlinedSecondary: {
          borderColor: '#8B6F47',
          color: '#8B6F47',
          '&:hover': {
            backgroundColor: 'rgba(139, 111, 71, 0.08)',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontSize: '0.9375rem',
          padding: '12px 16px',
        },
        standardInfo: {
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          color: '#1E40AF',
          border: '1px solid rgba(59, 130, 246, 0.3)',
        },
        standardSuccess: {
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          color: '#065F46',
          border: '1px solid rgba(16, 185, 129, 0.3)',
        },
        standardWarning: {
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          color: '#92400E',
          border: '1px solid rgba(245, 158, 11, 0.3)',
        },
        standardError: {
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          color: '#991B1B',
          border: '1px solid rgba(239, 68, 68, 0.3)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1A3A2E', // Deep Forest - dark, natural
          color: '#ffffff',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#F9FAFB', // Subtle background
            borderRadius: 8,
            transition: 'all 0.2s ease-in-out',
            '& fieldset': {
              borderColor: '#D4DDD7',
            },
            '&:hover': {
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderColor: '#2C5F4F', // Forest green
              },
            },
            '&.Mui-focused': {
              backgroundColor: '#FFFFFF',
              boxShadow: '0 0 0 3px rgba(44, 95, 79, 0.1)', // Forest green glow
              '& fieldset': {
                borderColor: '#2C5F4F',
                borderWidth: 2,
              },
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: '#F9FAFB',
          borderRadius: 8,
          transition: 'all 0.2s ease-in-out',
          '& fieldset': {
            borderColor: '#D4DDD7',
          },
          '&:hover': {
            backgroundColor: '#FFFFFF',
            '& fieldset': {
              borderColor: '#2C5F4F',
            },
          },
          '&.Mui-focused': {
            backgroundColor: '#FFFFFF',
            boxShadow: '0 0 0 3px rgba(44, 95, 79, 0.1)',
            '& fieldset': {
              borderColor: '#2C5F4F',
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#F9FAFB',
          borderRadius: 8,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: '#FFFFFF',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#2C5F4F',
            },
          },
          '&.Mui-focused': {
            backgroundColor: '#FFFFFF',
            boxShadow: '0 0 0 3px rgba(44, 95, 79, 0.1)',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#2C5F4F',
              borderWidth: 2,
            },
          },
        },
      },
    },
  },
});

export default theme; 